/**
 * Stone Investment Advanced Filters Modal
 * 
 * Full-screen filter modal with Stone Investment's luxury aesthetic:
 * - Greyscale colour palette
 * - Cinematic transitions
 * - Editorial typography
 */

'use client';

import { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { StoneFilterPanel, type FilterSection } from './StoneFilterPanel';

export interface StoneAdvancedFiltersModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;
  /**
   * Callback to close the modal
   */
  onClose: () => void;
  /**
   * Filter sections configuration
   */
  sections: FilterSection[];
  /**
   * Current filter values
   */
  values: Record<string, string | string[] | number | { min?: number; max?: number }>;
  /**
   * Callback when filters change
   */
  onChange: (values: Record<string, string | string[] | number | { min?: number; max?: number }>) => void;
  /**
   * Results count
   */
  resultsCount?: number;
  /**
   * Labels for translations
   */
  labels?: {
    title?: string;
    clearAll?: string;
    apply?: string;
    showResults?: string;
    close?: string;
  };
}

/**
 * StoneAdvancedFiltersModal - Full-screen filter modal with Stone Investment styling
 */
export function StoneAdvancedFiltersModal({
  isOpen,
  onClose,
  sections,
  values,
  onChange,
  resultsCount,
  labels = {
    title: 'Filter Properties',
    clearAll: 'Clear all',
    apply: 'Apply Filters',
    showResults: 'Show Results',
    close: 'Close',
  },
}: StoneAdvancedFiltersModalProps) {
  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  
  // Handle escape key
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);
  
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
    return undefined;
  }, [isOpen, handleKeyDown]);
  
  if (!isOpen) {
    return null;
  }
  
  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm animate-in fade-in duration-300"
        onClick={onClose}
      />
      
      {/* Modal Panel */}
      <div 
        className="absolute inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl animate-in slide-in-from-right duration-500"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-stone-200">
          <h2 
            className="font-serif text-2xl text-stone-900"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            {labels.title}
          </h2>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center border border-stone-300 text-stone-500 hover:text-stone-700 hover:border-stone-400 transition-colors"
            aria-label={labels.close}
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>
        
        {/* Content */}
        <div className="h-[calc(100vh-180px)] overflow-y-auto">
          <StoneFilterPanel
            sections={sections}
            values={values}
            onChange={onChange}
            labels={{
              title: '',
              clearAll: labels.clearAll || 'Clear all',
            }}
            className="border-0"
          />
        </div>
        
        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 px-8 py-6 border-t border-stone-200 bg-white">
          <div className="flex items-center justify-between gap-4">
            {resultsCount !== undefined && (
              <p className="font-sans text-sm text-stone-600">
                <span className="font-serif text-xl text-stone-900">{resultsCount}</span>
                <span className="ml-2">{resultsCount === 1 ? 'property' : 'properties'}</span>
              </p>
            )}
            
            <button
              onClick={onClose}
              className="flex-1 max-w-xs px-8 py-3 bg-stone-900 text-white font-sans text-sm uppercase tracking-[0.15em] hover:bg-stone-800 transition-colors"
            >
              {labels.showResults || labels.apply}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StoneAdvancedFiltersModal;
